import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { BranchTableViewModel, CommonNoteViewModel, BranchTableNoteViewModel, BranchTableCodeViewModel } from 'src/app/shared/models/autogenerated-piping';
import { CommentViewModel } from 'src/app/shared/models/comment-api.models';
import { faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { NotesSelectionModalComponent } from '../piping-summary/notes-selection-modal/notes-selection-modal.component';
import { BranchTableSyncService, BranchTableValidationResult } from 'src/app/shared/services/branch-table-sync.service';
import { first } from 'rxjs/operators';
import { BranchTableNoteModalComponent } from './branch-table-note-modal/branch-table-note-modal.component';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';

// Note: Modals will be implemented separately to avoid circular dependencies

@Component({
  selector: 'app-branch-tables',
  templateUrl: './branch-tables.component.html',
  styleUrls: ['./branch-tables.component.scss'],
  standalone: false
})
export class BranchTablesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() canEdit$: Observable<boolean>;
  /** True for any user who arrived from the revisions dashboard (authors + reviewers).
   *  Used to show the comments section even for reviewers who cannot edit fields. */
  @Input() isInRevisionWorkflow: boolean = false;
  @Input() branchTableViewModelList: BranchTableViewModel[] = [];
  /** The previous revision's branch tables, used to render diff cells in compare mode. */
  @Input() previousBranchTables: BranchTableViewModel[] = [];
  /** When `true`, edit controls are hidden and each table is shown as a side-by-side diff against `previousBranchTables`. */
  @Input() isCompareMode: boolean = false;
  @Input() pipeClassId: string;
  @Input() revisionId: string; // For comments section
  @Input() comments: CommentViewModel[] = []; // Comments for auto-save
  @Input() commentsSaving$: Observable<boolean>; // Auto-save loading state
  @Input() commentsLoading$: Observable<boolean>; // True while comments are being fetched
  @Output() addBranchTablesEvent = new EventEmitter<string>();
  @Output() notesAddedEvent = new EventEmitter<{notes: CommonNoteViewModel[], origin: string}>();
  @Output() notesRemovedEvent = new EventEmitter<{notes: CommonNoteViewModel[], origin: string}>();
  @Output() commentsChanged = new EventEmitter<CommentViewModel[]>(); // Comments change event
  @Output() saveCommentsRequested = new EventEmitter<void>(); // Auto-save trigger
  @Output() branchTableSynced = new EventEmitter<{oldId: string, newTable: BranchTableViewModel}>(); // Sync event for cache update
  @Output() branchTableRemoved = new EventEmitter<BranchTableViewModel>(); // Table removal event for caching
  @Output() removeBranchTableRequested = new EventEmitter<number>();

  // FontAwesome icons
  faPlus = faPlus;
  faTrashCan = faTrashCan;

  // Branch Tables functionality
  selectedBranchTables: any[] = [];
  newFreeTextNote: { [key: string]: string } = {};
  globalCommonNotes: any[] = [];

  // Branch Table Sync functionality
  branchTableSyncStates: Map<number, BranchTableValidationResult> = new Map();
  branchTableSyncLoading: Map<number, boolean> = new Map();

  /** True when the current user has the piping dictionary writer role and can open the branch table editor. */
  canEditBranchTableDictionary: boolean = false;

  /** BroadcastChannel used to receive branch-table-saved notifications from the dictionary editor tab. */
  private branchTableUpdateChannel: BroadcastChannel | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private branchTableSyncService: BranchTableSyncService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.canEditBranchTableDictionary =
      this.userService.isRoleExists(Constants.pipingDictionaryWriter) ||
      this.userService.isRoleExists(Constants.admin) ||
      this.userService.isRoleExists(Constants.scanner);
    this.listenForBranchTableDictionaryUpdates();
  }

  /**
   * Opens the branch table dictionary editor in a new tab for the given table.
   * Uses localStorage to pass the table data (the same mechanism the admin dictionary list uses).
   * Only available to users with the pipingDictionaryWriter role.
   */
  openBranchTableEditor(branch: BranchTableViewModel): void {
    localStorage.setItem('branchTable', JSON.stringify(branch));
    window.open('path/branchTableAddEdit', '_blank');
  }

  /**
   * Subscribes to cross-tab branch-table-saved events via BroadcastChannel.
   * When the dictionary editor in another tab saves a branch table that is referenced
   * by this revision, the cached validation state is cleared and re-validated so the
   * sync button becomes enabled without requiring a page refresh.
   */
  private listenForBranchTableDictionaryUpdates(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.branchTableUpdateChannel = new BroadcastChannel('branch-table-dictionary-updated');
    this.branchTableUpdateChannel.onmessage = (event: MessageEvent) => {
      const branchTableId: string | undefined = (event.data as { branchTableId?: string })?.branchTableId;
      if (!branchTableId || !this.branchTableViewModelList?.length) {
        return;
      }
      const tableIndex: number = this.branchTableViewModelList
        .findIndex(t => t.branchTableId === branchTableId);
      if (tableIndex >= 0) {
        // Clear cached state so validateBranchTable fetches fresh data
        this.branchTableSyncStates.delete(tableIndex);
        this.validateBranchTable(tableIndex);
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['branchTableViewModelList']) {

      // Clear sync states when branch table list changes
      this.branchTableSyncStates.clear();
      this.branchTableSyncLoading.clear();

      // Validate all branch tables
      setTimeout(() => {
        this.validateAllBranchTables();
      }, 100);

      // Force multiple change detection cycles to ensure all child components update
      this.cdr.detectChanges();

      // Use requestAnimationFrame for better rendering timing
      requestAnimationFrame(() => {
        this.cdr.detectChanges();

        // Final check after another tick
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 10);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.branchTableUpdateChannel) {
      this.branchTableUpdateChannel.close();
      this.branchTableUpdateChannel = null;
    }
  }

  openAddBranchTableModal(): void {
    this.addBranchTablesEvent.emit('Branch Tables');
  }

  /**
   * Returns the previous revision's branch table that corresponds to the given current table.
   *
   * Matching priority:
   * 1. `branchTableId` — the stable catalog identifier; identical in both revisions when the
   *    same catalog entry is referenced, regardless of display-name changes.
   * 2. `name` — fallback for older data that may lack a catalog ID.
   *
   * Returns `undefined` when no match is found (i.e. the table was added in the current revision
   * or a completely different branch table was selected).
   */
  getPreviousBranchTable(currentTable: BranchTableViewModel): BranchTableViewModel | undefined {
    if (!this.previousBranchTables?.length) { return undefined; }

    // Match by name only — branchTableId is a catalog/dictionary identifier and is not
    // guaranteed to be the same value across revisions that reference the same logical table.
    if (currentTable.name) {
      return this.previousBranchTables.find(t => t.name === currentTable.name);
    }

    return undefined;
  }

  /**
   * Returns previous revision branch tables that have no counterpart in the current revision.
   * Used in compare mode to render removed tables in red (all cells shown as red strikethrough).
   *
   * A previous table is considered "removed" when none of the current revision's tables
   * share the same name.
   */
  getDeletedPreviousTables(): BranchTableViewModel[] {
    if (!this.isCompareMode || !this.previousBranchTables?.length) { return []; }
    const currentNames: Set<string> = new Set(
      (this.branchTableViewModelList || []).map(t => t.name || '').filter(Boolean)
    );
    return this.previousBranchTables.filter(t => t.name && !currentNames.has(t.name));
  }

  /**
   * Returns legend codes for a branch table as display strings (`"code — description"`)
   * suitable for use with `app-revision-list-field` in compare mode.
   */
  getLegendDisplayItems(branchTable: BranchTableViewModel | undefined): string[] {
    if (!branchTable?.codes) { return []; }
    return branchTable.codes
      .filter(c => c.code)
      .map(c => c.description ? `${c.code} — ${c.description}` : (c.code || ''));
  }

  /**
   * Returns all common note codeIds for a branch table (both editable relation notes and
   * read-only dictionary notes), sorted alphabetically, for compare mode diffing.
   */
  getBranchTableCommonNoteCodes(branchTable: BranchTableViewModel | undefined): string[] {
    const relationNotes: string[] = (branchTable?.relationCommonNotes || []).map(n => n.codeId || '');
    const dictionaryNotes: string[] = (branchTable?.commonNotes || []).map(n => n.codeId || '');
    return [...relationNotes, ...dictionaryNotes].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }

  /**
   * Returns the additional (free-text) note strings for a branch table,
   * for use with `app-revision-list-field` in compare mode.
   */
  getBranchTableAdditionalNoteTexts(branchTable: BranchTableViewModel | undefined): string[] {
    return (branchTable?.relationAdditionalNotes || []).map(n => n.note || '').filter(Boolean);
  }

  removeBranchTable(index: number): void {
    if (index >= 0 && index < this.branchTableViewModelList.length) {
      const removedTable = this.branchTableViewModelList[index];


      this.branchTableRemoved.emit(removedTable);

      this.removeBranchTableRequested.emit(index);
    }
  }

  viewBranchTable(branchTable: any): void {
    console.log('Viewing branch table:', branchTable);
    // Implement view logic
  }

  editBranchTable(branchTable: any): void {
    console.log('Editing branch table:', branchTable);
    // Implement edit logic
  }

  addCommonNote(branchTableId: string): void {
    // Temporary implementation - replace with actual modal later

    // Mock adding a common note for now
    const mockNote = {
      id: `note-${Date.now()}`,
      code: `NOTE-${Math.floor(Math.random() * 1000)}`,
      note: 'This is a mock common note for testing purposes'
    };

  }

  addFreeTextNote(branchTableId: string): void {
    const noteText = this.newFreeTextNote[branchTableId];
    if (noteText && noteText.trim()) {
      // Implement free text note logic
      this.newFreeTextNote[branchTableId] = '';
    }
  }

  /**
   * Open common note selector modal for a specific branch table
   */
  openAddCommonNoteModal(branchTableId: string): void {
    // Find the branch table
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      return;
    }

    // Check if branch table needs sync first
    if (this.isBranchTableOutOfDate(branchTableId)) {
      return;
    }

    // Get table name for display
    const tableName = branchTable.name || `Branch Table ${branchTableId}`;

    // Get existing notes for this branch table from relationCommonNotes
    const existingNotes = (branchTable.relationCommonNotes || []).map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true
    }));

    // Open the notes selection modal
    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: existingNotes,
        title: `Select Notes for ${tableName}`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && Array.isArray(result)) {
        this.handleBranchTableNotesResult(branchTableId, existingNotes, result);
      }
    });
  }

  /**
   * Handle the result from the branch table notes modal
   */
  private handleBranchTableNotesResult(branchTableId: string, previousNotes: any[], currentNotes: any[]): void {
    // Find added and removed notes
    const addedNotes = currentNotes.filter(newNote =>
      !previousNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
    );

    const removedNotes = previousNotes.filter(oldNote =>
      !currentNotes.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
    );

    // Update the branch table's relation common notes (editable ones)
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (branchTable) {
      // Convert result to CommonNoteViewModel format and save to relationCommonNotes
      branchTable.relationCommonNotes = currentNotes.map(note => new CommonNoteViewModel({
        noteId: note.id,
        codeId: note.code,
        note: note.note,
        isActive: true,
        isArchived: false,
        isReferenced: false,
        updated: new Date()
      }));

      // Emit events for parent component (if needed)
      if (addedNotes.length > 0) {
        const addedAsCommonNotes = addedNotes.map(note => new CommonNoteViewModel({
          noteId: note.id,
          codeId: note.code,
          note: note.note,
          isActive: true,
          isArchived: false,
          isReferenced: false,
          updated: new Date()
        }));

        this.notesAddedEvent.emit({
          notes: addedAsCommonNotes,
          origin: `branch-table-${branchTableId}`
        });
      }

      if (removedNotes.length > 0) {
        const removedAsCommonNotes = removedNotes.map(note => new CommonNoteViewModel({
          noteId: note.id,
          codeId: note.code,
          note: note.note,
          isActive: true,
          isArchived: false,
          isReferenced: false,
          updated: new Date()
        }));

        this.notesRemovedEvent.emit({
          notes: removedAsCommonNotes,
          origin: `branch-table-${branchTableId}`
        });
      }

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  /**
   * Get all common notes for a branch table (merged: relationCommonNotes + commonNotes)
   * relationCommonNotes are editable, commonNotes are read-only from dictionary
   * Notes are sorted by codeId
   */
  getAllCommonNotesForBranchTable(branchTableId: string): CommonNoteViewModel[] {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      return [];
    }

    // Merge both lists: relationCommonNotes (editable) + commonNotes (read-only)
    const relationNotes = branchTable.relationCommonNotes || [];
    const dictionaryNotes = branchTable.commonNotes || [];

    // Combine both arrays and sort by codeId
    const allNotes = [...relationNotes, ...dictionaryNotes];

    return allNotes.sort((a, b) => {
      const codeA = (a.codeId || '').toLowerCase();
      const codeB = (b.codeId || '').toLowerCase();
      return codeA.localeCompare(codeB);
    });
  }

  /**
   * Check if a note is editable (comes from relationCommonNotes)
   */
  isNoteEditable(branchTableId: string, note: CommonNoteViewModel): boolean {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      return false;
    }

    // A note is editable if it exists in relationCommonNotes
    const relationNotes = branchTable.relationCommonNotes || [];
    return relationNotes.some(n =>
      (n.noteId && n.noteId === note.noteId) ||
      (n.note === note.note && n.codeId === note.codeId)
    );
  }

  /**
   * Remove a common note from a specific branch table
   * Only notes from relationCommonNotes can be removed (editable ones)
   */
  removeCommonNoteFromBranchTable(branchTableId: string, note: CommonNoteViewModel): void {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      return;
    }

    // Check if branch table needs sync first
    if (this.isBranchTableOutOfDate(branchTableId)) {
      return;
    }

    // Check if the note is editable (from relationCommonNotes)
    if (!this.isNoteEditable(branchTableId, note)) {
      console.warn('Cannot remove read-only note from dictionary');
      return;
    }

    // Remove from relationCommonNotes only
    if (branchTable.relationCommonNotes) {
      const noteIndex = branchTable.relationCommonNotes.findIndex(n =>
        (n.noteId && n.noteId === note.noteId) ||
        (n.note === note.note && n.codeId === note.codeId)
      );

      if (noteIndex >= 0) {
        branchTable.relationCommonNotes.splice(noteIndex, 1);

        // Emit removal event for parent component
        this.notesRemovedEvent.emit({
          notes: [note],
          origin: `branch-table-${branchTableId}`
        });

        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Get additional notes for a branch table (relationAdditionalNotes)
   */
  getAdditionalNotesForBranchTable(branchTableId: string): BranchTableNoteViewModel[] {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    return branchTable?.relationAdditionalNotes || [];
  }

  /**
   * Open modal to add a new branch table note
   */
  openAddAdditionalNoteModal(branchTableId: string): void {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      return;
    }

    // Check if branch table needs sync first
    if (this.isBranchTableOutOfDate(branchTableId)) {
      return;
    }

    const dialogRef = this.dialog.open(BranchTableNoteModalComponent, {
      data: {
        title: 'Add Branch Table Note',
        note: ''
      },
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minHeight: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return; // User cancelled
      }

      // Create new note (without type field)
      const newNote = new BranchTableNoteViewModel({
        note: result.note.trim()
      });

      // Add to relationAdditionalNotes array
      if (!branchTable.relationAdditionalNotes) {
        branchTable.relationAdditionalNotes = [];
      }
      branchTable.relationAdditionalNotes.push(newNote);


      // Force change detection
      this.cdr.detectChanges();

      // Emit event for parent to save
      // TODO: Emit proper event when parent component is ready to handle it
    });
  }

  /**
   * Open modal to edit an existing branch table note
   */
  openEditAdditionalNoteModal(branchTableId: string, noteIndex: number): void {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable || !branchTable.relationAdditionalNotes) {
      return;
    }

    // Check if branch table needs sync first
    if (this.isBranchTableOutOfDate(branchTableId)) {
      return;
    }

    const currentNote = branchTable.relationAdditionalNotes[noteIndex];
    if (!currentNote) {
      return;
    }

    const dialogRef = this.dialog.open(BranchTableNoteModalComponent, {
      data: {
        title: 'Edit Branch Table Note',
        note: currentNote.note || ''
      },
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minHeight: '400px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return; // User cancelled
      }

      // Update note (without type field)
      currentNote.note = result.note.trim();


      // Force change detection
      this.cdr.detectChanges();

      // Emit event for parent to save
      // TODO: Emit proper event when parent component is ready to handle it
    });
  }

  /**
   * Remove an additional note from a branch table
   */
  removeAdditionalNoteFromBranchTable(branchTableId: string, noteIndex: number): void {
    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable || !branchTable.relationAdditionalNotes) {
      return;
    }

    // Check if branch table needs sync first
    if (this.isBranchTableOutOfDate(branchTableId)) {
      return;
    }

    const noteToRemove = branchTable.relationAdditionalNotes[noteIndex];
    if (!noteToRemove) {
      return;
    }

    // Confirm deletion
    const confirmed = confirm(`Are you sure you want to remove this note?\n\n"${noteToRemove.note}"`);
    if (!confirmed) {
      return;
    }

    // Remove from array
    branchTable.relationAdditionalNotes.splice(noteIndex, 1);


    // Force change detection
    this.cdr.detectChanges();

    // Emit event for parent to save
    // TODO: Emit proper event when parent component is ready to handle it
  }

  // Comment handling methods for auto-save
  onCommentsChanged(comments: CommentViewModel[]): void {
    this.commentsChanged.emit(comments);
  }

  onSaveCommentsRequested(): void {
    this.saveCommentsRequested.emit();
  }

  // TrackBy function for branch tables to help Angular detect changes
  trackBranchTable(index: number, table: BranchTableViewModel): string {
    const trackKey = `${table.branchTableId}_${table.name}_${index}`;
    return trackKey;
  }

  // Branch Table Sync Methods
  /**
   * Validates all branch tables in the branchTableViewModelList
   */
  validateAllBranchTables(): void {

    if (!this.branchTableViewModelList || this.branchTableViewModelList.length === 0) {
      console.log('[BranchTables] No branch tables to validate (list is empty)');
      return;
    }


    this.branchTableViewModelList.forEach((table, index) => {
      console.log(`🔍 [BranchTables] Checking table at index ${index}:`, {
        name: table.name,
        id: table.branchTableId,
        alreadyValidated: this.branchTableSyncStates.has(index)
      });

      if (!this.branchTableSyncStates.has(index) && table.branchTableId) {
        this.validateBranchTable(index);
      }
    });
  }

  /**
   * Validates a branch table to check if it's the latest version
   */
  private validateBranchTable(tableIndex: number): void {

    const branchTable = this.branchTableViewModelList[tableIndex];
    if (!branchTable || !branchTable.branchTableId) {
      return;
    }

    const existingState = this.branchTableSyncStates.get(tableIndex);
    if (existingState) {
      return;
    }

    this.branchTableSyncLoading.set(tableIndex, true);

    this.branchTableSyncService.validateAndGetLatestBranchTable(branchTable.branchTableId)
      .pipe(first())
      .subscribe({
        next: (result) => {
          this.branchTableSyncStates.set(tableIndex, result);
          this.branchTableSyncLoading.set(tableIndex, false);

          if (!result.isCurrent && result.latestVersion) {
          }

          this.cdr.detectChanges();
        },
        error: (error) => {
          this.branchTableSyncLoading.set(tableIndex, false);
          const fallbackState = {
            isCurrent: true,
            message: 'Validation failed'
          };
          this.branchTableSyncStates.set(tableIndex, fallbackState);
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Returns `true` if any branch table in the list is confirmed out of sync.
   * Used by the parent component to gate the "Confirm Info to Review" action.
   */
  hasUnsyncedBranchTables(): boolean {
    if (!this.branchTableViewModelList?.length) {
      return false;
    }
    return this.branchTableViewModelList.some((_, index) => {
      const syncState: BranchTableValidationResult | undefined = this.branchTableSyncStates.get(index);
      return syncState !== undefined && !syncState.isCurrent;
    });
  }

  /**
   * Checks if branch table sync is enabled (not current version)
   */
  isBranchTableSyncEnabled(tableIndex: number): boolean {
    // Don't validate here - it should only be done in ngOnChanges
    // This method is called frequently during rendering
    const syncState = this.branchTableSyncStates.get(tableIndex);
    const isEnabled = syncState ? !syncState.isCurrent : false;

    // Only log when state changes to avoid spam
    if (syncState && !syncState.isCurrent) {
    }

    return isEnabled;
  }

  /**
   * Check if branch table is out of date and needs sync before editing
   */
  isBranchTableOutOfDate(branchTableId: string): boolean {
    const tableIndex = this.branchTableViewModelList.findIndex(bt => bt.branchTableId === branchTableId);
    if (tableIndex === -1) {
      return false;
    }
    return this.isBranchTableSyncEnabled(tableIndex);
  }

  /**
   * Gets the tooltip for branch table sync button
   */
  getBranchTableSyncTooltip(tableIndex: number): string {
    const syncState = this.branchTableSyncStates.get(tableIndex);

    if (!syncState) {
      return 'Checking for updates...';
    }

    if (this.branchTableSyncLoading.get(tableIndex)) {
      return 'Checking for updates...';
    }

    if (syncState.isCurrent) {
      return 'Branch table is up to date';
    }

    return syncState.message || 'Update to latest version available';
  }

  /**
   * Checks if branch table sync is loading
   */
  isBranchTableSyncLoading(tableIndex: number): boolean {
    return this.branchTableSyncLoading.get(tableIndex) || false;
  }

  /**
   * Syncs branch table to latest version (replaces with latest preserving notes)
   */
  syncBranchTable(tableIndex: number): void {

    const syncState = this.branchTableSyncStates.get(tableIndex);

    if (!syncState) {
      return;
    }

    if (syncState.isCurrent) {
      return;
    }

    if (!syncState.latestVersion) {
      return;
    }

    const currentBranchTable = this.branchTableViewModelList[tableIndex];
    console.log(`📝 [BranchTables] Current table:`, {
      name: currentBranchTable.name,
      id: currentBranchTable.branchTableId,
      commonNotesCount: currentBranchTable.commonNotes?.length || 0,
      relationCommonNotesCount: currentBranchTable.relationCommonNotes?.length || 0,
      relationAdditionalNotesCount: currentBranchTable.relationAdditionalNotes?.length || 0,
      codesCount: currentBranchTable.codes?.length || 0,
      codesWithNotes: currentBranchTable.codes?.filter(c => c.commonNotes && c.commonNotes.length > 0).length || 0
    });

    // Preserve ALL types of user data from current table
    const preservedCommonNotes = currentBranchTable.commonNotes || [];
    const preservedRelationCommonNotes = currentBranchTable.relationCommonNotes || [];
    const preservedRelationAdditionalNotes = currentBranchTable.relationAdditionalNotes || [];

    // Convert latest version to proper instance if needed
    const latestVersion = BranchTableViewModel.fromJS(syncState.latestVersion);
    // Create new table with latest version data
    // Note: commonNotes come from the new dictionary version (read-only)
    // relationCommonNotes and relationAdditionalNotes need to be migrated to the new branchTableId
    latestVersion.relationCommonNotes = preservedRelationCommonNotes;
    latestVersion.relationAdditionalNotes = preservedRelationAdditionalNotes;


    // Replace the table in the array
    this.branchTableViewModelList[tableIndex] = latestVersion;

    // Trigger array change detection by creating new reference
    this.branchTableViewModelList = [...this.branchTableViewModelList];

    // Emit event to notify parent that table was synced (for cache update)
    this.branchTableSynced.emit({
      oldId: currentBranchTable.branchTableId,
      newTable: latestVersion
    });

    const allNotesToAdd: any[] = [];

    if (latestVersion.commonNotes && latestVersion.commonNotes.length > 0) {
      allNotesToAdd.push(...latestVersion.commonNotes);
    }

    if (latestVersion.relationCommonNotes && latestVersion.relationCommonNotes.length > 0) {
      allNotesToAdd.push(...latestVersion.relationCommonNotes);
    }

    // Also collect common notes from legend codes (both dictionary and revision)
    if (latestVersion.codes && latestVersion.codes.length > 0) {
      latestVersion.codes.forEach(code => {
        // Dictionary notes
        if (code.commonNotes && code.commonNotes.length > 0) {
          // Sort dictionary notes by codeId
          code.commonNotes.sort((a, b) => {
            const codeA = a.codeId || '';
            const codeB = b.codeId || '';
            return codeA.localeCompare(codeB);
          });
          allNotesToAdd.push(...code.commonNotes);
        }
        // Revision notes
        if (code.relationCommonNotes && code.relationCommonNotes.length > 0) {
          // Sort revision notes by codeId
          code.relationCommonNotes.sort((a, b) => {
            const codeA = a.codeId || '';
            const codeB = b.codeId || '';
            return codeA.localeCompare(codeB);
          });
          allNotesToAdd.push(...code.relationCommonNotes);
        }
      });
    }

    if (allNotesToAdd.length > 0) {
      this.notesAddedEvent.emit({
        notes: allNotesToAdd,
        origin: `branch-table-${latestVersion.branchTableId}-sync`
      });
    }

    // Update sync state to current
    this.branchTableSyncStates.set(tableIndex, {
      isCurrent: true,
      latestVersion: latestVersion,
      message: 'Branch table is up to date'
    });

    // Force complete re-render
    this.cdr.markForCheck();
    this.cdr.detectChanges();

    // Additional force render after a tick to ensure UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }


  onEditLegendNotes(event: { branchTableId: string, legendCode: BranchTableCodeViewModel }): void {
    const { branchTableId, legendCode } = event;

    const branchTable = this.branchTableViewModelList.find(bt => bt.branchTableId === branchTableId);
    if (!branchTable) {
      console.warn('Branch table not found:', branchTableId);
      return;
    }

    if (this.isBranchTableOutOfDate(branchTableId)) {
      console.warn('Cannot edit legend notes when branch table is out of date');
      return;
    }

    const legendName = `${legendCode.code} - ${legendCode.description}`;

    const dictionaryNotes = (legendCode.commonNotes || []).map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true,
      readonly: true
    }));

    const revisionNotes = (legendCode.relationCommonNotes || []).map(note => ({
      id: note.noteId || '',
      code: note.codeId || '',
      note: note.note || '',
      category: 'Common',
      selected: true,
      readonly: false
    }));

    const allExistingNotes = [...dictionaryNotes, ...revisionNotes];

    console.log(`📝 [BranchTables] Legend notes for ${legendCode.code}:`, {
      dictionary: dictionaryNotes.length,
      revision: revisionNotes.length,
      total: allExistingNotes.length
    });

    const dialogRef = this.dialog.open(NotesSelectionModalComponent, {
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      maxHeight: '90vh',
      minWidth: '320px',
      minHeight: '400px',
      disableClose: true,
      data: {
        availableNotes: [],
        selectedNotes: allExistingNotes,
        title: `Select Notes for Legend: ${legendName}`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && Array.isArray(result)) {
        this.handleLegendNotesResult(branchTableId, legendCode, allExistingNotes, result);
      }
    });
  }


  private handleLegendNotesResult(
    branchTableId: string,
    legendCode: BranchTableCodeViewModel,
    previousNotes: any[],
    currentNotes: any[]
  ): void {
    const previousEditableNotes = previousNotes.filter(n => !n.readonly);
    const currentEditableNotes = currentNotes.filter(n => !n.readonly);

    const addedNotes = currentEditableNotes.filter(newNote =>
      !previousEditableNotes.some(oldNote => oldNote.id === newNote.id && oldNote.code === newNote.code)
    );

    const removedNotes = previousEditableNotes.filter(oldNote =>
      !currentEditableNotes.some(newNote => newNote.id === oldNote.id && newNote.code === oldNote.code)
    );


    // IMPORTANT: Only update relationCommonNotes (revision notes)
    // commonNotes (dictionary notes) remain unchanged
    legendCode.relationCommonNotes = currentEditableNotes.map(note => new CommonNoteViewModel({
      noteId: note.id,
      codeId: note.code,
      note: note.note,
      isActive: true,
      isArchived: false,
      isReferenced: false,
      updated: new Date()
    }));

    // Sort relation common notes by codeId
    if (legendCode.relationCommonNotes && legendCode.relationCommonNotes.length > 0) {
      legendCode.relationCommonNotes.sort((a, b) => {
        const codeA = a.codeId || '';
        const codeB = b.codeId || '';
        return codeA.localeCompare(codeB);
      });
    }

    // Emit added notes
    if (addedNotes.length > 0) {
      const addedAsCommonNotes = addedNotes.map(note => new CommonNoteViewModel({
        noteId: note.id,
        codeId: note.code,
        note: note.note,
        isActive: true,
        isArchived: false,
        isReferenced: false,
        updated: new Date()
      }));

      this.notesAddedEvent.emit({
        notes: addedAsCommonNotes,
        origin: `legend-${legendCode.id || legendCode.code}`
      });
    }

    // Emit removed notes
    if (removedNotes.length > 0) {
      const removedAsCommonNotes = removedNotes.map(note => new CommonNoteViewModel({
        noteId: note.id,
        codeId: note.code,
        note: note.note,
        isActive: true,
        isArchived: false,
        isReferenced: false,
        updated: new Date()
      }));

      this.notesRemovedEvent.emit({
        notes: removedAsCommonNotes,
        origin: `legend-${legendCode.id || legendCode.code}`
      });
    }

    this.cdr.detectChanges();
  }

}
